// src/pages/Newsletter/NewsletterUnsubscribePage.tsx
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, MailX, ArrowLeft } from 'lucide-react';
import { config } from '../../config';

const API_BASE_URL = config.apiBaseUrl;

const NewsletterUnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'confirm' | 'loading' | 'success' | 'error'>('confirm');
  const [message, setMessage] = useState('');

  const email = searchParams.get('email');

  useEffect(() => {
    if (!email) {
      setStatus('error');
      setMessage('Invalid unsubscribe link. Missing email address.');
    }
  }, [email]);

  const handleUnsubscribe = async () => {
    if (!email) return;

    setStatus('loading');

    try {
      const response = await fetch(
        `${API_BASE_URL}/email-notifications/newsletter/unsubscribe/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      if (response.ok) {
        setStatus('success');
        setMessage('You have been successfully unsubscribed from our newsletter.');
      } else {
        const data = await response.json().catch(() => ({}));
        setStatus('error');
        setMessage(data.message || 'Failed to unsubscribe. Please try again later.');
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      setStatus('error');
      setMessage('An error occurred. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back to Home */}
        <Link
          to="/"
          className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 text-center">
          {/* Icon */}
          <div className="mb-6">
            {status === 'confirm' && (
              <div className="w-16 h-16 mx-auto bg-yellow-500/20 rounded-full flex items-center justify-center">
                <MailX className="w-8 h-8 text-yellow-500" />
              </div>
            )}
            {status === 'loading' && (
              <div className="w-16 h-16 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            {status === 'confirm' && 'Unsubscribe from Newsletter'}
            {status === 'loading' && 'Processing...'}
            {status === 'success' && 'Unsubscribed Successfully'}
            {status === 'error' && 'Unsubscribe Failed'}
          </h1>

          {/* Message */}
          <p className="text-gray-400 mb-6">
            {status === 'confirm' && (
              <>
                Are you sure you want to unsubscribe <span className="text-white font-medium">{email}</span> from our newsletter?
              </>
            )}
            {status === 'loading' && 'Please wait while we process your request.'}
            {(status === 'success' || status === 'error') && message}
          </p>

          {/* Confirm Buttons */}
          {status === 'confirm' && email && (
            <div className="flex gap-4 justify-center">
              <Link
                to="/"
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all duration-200"
              >
                Cancel
              </Link>
              <button
                onClick={handleUnsubscribe}
                className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-all duration-200"
              >
                Unsubscribe
              </button>
            </div>
          )}

          {/* Success Content */}
          {status === 'success' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                You will no longer receive newsletter emails from StrymHub. You can always subscribe again from our homepage.
              </p>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-red-500/25"
              >
                Go to Home
              </Link>
            </div>
          )}

          {/* Error Content */}
          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                If you continue to have issues, please contact our support team.
              </p>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-red-500/25"
              >
                Go to Home
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © {new Date().getFullYear()} StrymHub. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default NewsletterUnsubscribePage;
